import { useEffect, useRef, useState } from 'react';
import { CmmFileType, CmmFileTypes, ValidationCell } from '../../../../shared/types/cmmConfig.ts';
import { maxBy } from '../../../../shared/utils/arrayUtils.ts';
import { Input } from '../../../components/input.tsx';
import { LinkButton } from '../../../components/linkButton.tsx';
import { ModalDialog } from '../../../components/modalDialog.tsx';
import { CmmConfig } from '../../../types/sharedTypeImpl.ts';
import { trpc } from '../../../utils/trpc/trpc.ts';
import { excelColumnToIndex, excelCoordsToIndexes, excelIndexToColumn, indexesToExcelCoords } from './excelCoords.ts';


interface CmmConfigForm {
    name?: string;
    fileType?: CmmFileType;
    metadataRows?: number;
    equipmentNameCell?: string;
    positionNumberColumn?: string;
    positionValueColumn?: string;
    validationCells?: { coordinates: string; value: string }[];
}

interface Props {
    cmmConfigUnderEdit?: CmmConfig;
    cmmConfigs: CmmConfig[];
    onCmmConfigSaved: (cmmconfig: CmmConfig) => void;
    onClose: () => void;
}

export function CmmConfigDialog({ cmmConfigUnderEdit, cmmConfigs, onCmmConfigSaved, onClose }: Props) {
    const formRef = useRef<HTMLFormElement>(null);
    const [cmmConfig, setCmmConfig] = useState<CmmConfigForm>({});

    const createCmmConfig = trpc.cmmConfigs.create.useMutation();
    const editCmmConfig = trpc.cmmConfigs.replace.useMutation();

    useEffect(() => {
        if (cmmConfigUnderEdit) {
            setCmmConfig({
                name: cmmConfigUnderEdit.name,
                fileType: cmmConfigUnderEdit.fileType,
                metadataRows: cmmConfigUnderEdit.metadataRows,
                equipmentNameCell: cmmConfigUnderEdit.equipmentNameCell
                    ? indexesToExcelCoords(cmmConfigUnderEdit.equipmentNameCell.row, cmmConfigUnderEdit.equipmentNameCell.column)
                    : undefined,
                positionNumberColumn: excelIndexToColumn(cmmConfigUnderEdit.positionNumberColumn),
                positionValueColumn: excelIndexToColumn(cmmConfigUnderEdit.positionValueColumn),
                validationCells: cmmConfigUnderEdit.validationCells.map((cell) => ({
                    coordinates: indexesToExcelCoords(cell.row, cell.column),
                    value: cell.value,
                })),
            });
        } else {
            setCmmConfig({});
        }
    }, [cmmConfigUnderEdit]);

    async function handleSubmit() {
        let positionNumberColumn: number;
        let positionValueColumn: number;
        let validationCells: ValidationCell[];
        let equipmentNameCell: { row: number; column: number } | undefined;
        try {
            positionNumberColumn = excelColumnToIndex(cmmConfig.positionNumberColumn!);
            positionValueColumn = excelColumnToIndex(cmmConfig.positionValueColumn!);
            validationCells =
                cmmConfig.validationCells?.map((cell) => ({
                    ...excelCoordsToIndexes(cell.coordinates),
                    value: cell.value,
                })) ?? [];
            equipmentNameCell = cmmConfig.equipmentNameCell?.trim()
                ? excelCoordsToIndexes(cmmConfig.equipmentNameCell.trim().toUpperCase())
                : undefined;
        } catch (error) {
            alert(error instanceof Error ? error.message : 'Invalid input');
            return;
        }

        const maxOrder = maxBy(cmmConfigs, (it) => it.order)?.order ?? 0;
        const cmmConfigToSave: CmmConfig = {
            _id: cmmConfigUnderEdit?._id,
            name: cmmConfig.name!.trim(),
            fileType: cmmConfig.fileType!,
            metadataRows: cmmConfig.metadataRows!,
            positionNumberColumn,
            positionValueColumn,
            validationCells,
            equipmentNameCell,
            isActive: cmmConfigUnderEdit?.isActive ?? true,
            order: cmmConfigUnderEdit?.order ?? maxOrder + 1,
        };
        const savedCmmConfig = cmmConfigUnderEdit
            ? await editCmmConfig.mutateAsync(cmmConfigToSave)
            : await createCmmConfig.mutateAsync(cmmConfigToSave);
        onCmmConfigSaved(savedCmmConfig);
        onClose();
    }

    const cmmFileType = CmmFileTypes.find((it) => it.id === cmmConfig.fileType);
    const isSubmitting = createCmmConfig.isPending || editCmmConfig.isPending;

    return (
        <ModalDialog frameStyle={{ width: '360px' }} isOpen={true} onCancel={onClose}>
            <form
                ref={formRef}
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit();
                }}
            >
                <div className='dialog-form__field--with-margins'>
                    <div>Name</div>
                    <input
                        className='dialog-form__input'
                        value={cmmConfig.name ?? ''}
                        onChange={(e) => setCmmConfig({ ...cmmConfig, name: e.target.value })}
                        pattern='.*\S.*'
                        required
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div>File type</div>
                    <select
                        className='dialog-form__input'
                        value={cmmFileType?.id ?? ''}
                        onChange={(e) => setCmmConfig({ ...cmmConfig, fileType: e.target.value as CmmFileType })}
                        required
                    >
                        <option value={''} disabled hidden />
                        {CmmFileTypes.map((fileType) => (
                            <option key={fileType.id} value={fileType.id}>
                                {fileType.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div>Metadata row count</div>
                    <Input
                        className='dialog-form__input'
                        type='number'
                        min={0}
                        step={1}
                        value={cmmConfig.metadataRows ?? ''}
                        onChange={(e) => setCmmConfig({ ...cmmConfig, metadataRows: +e.target.value })}
                        required
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div>Position number column (A-Z)</div>
                    <input
                        className='dialog-form__input'
                        type='text'
                        pattern='^[A-Z]+$'
                        minLength={1}
                        maxLength={1}
                        value={cmmConfig.positionNumberColumn}
                        onChange={(e) => setCmmConfig({ ...cmmConfig, positionNumberColumn: e.target.value.toUpperCase() })}
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div>Position value column (A-Z)</div>
                    <input
                        className='dialog-form__input'
                        type='text'
                        pattern='^[A-Z]+$'
                        minLength={1}
                        maxLength={1}
                        value={cmmConfig.positionValueColumn}
                        onChange={(e) => setCmmConfig({ ...cmmConfig, positionValueColumn: e.target.value.toUpperCase() })}
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div className='flex--horz' style={{ justifyContent: 'space-between' }}>
                        <div>Validation cells</div>
                        <LinkButton
                            children='Add'
                            onClick={() => {
                                const newCell: { coordinates: string; value: string } = {
                                    coordinates: '',
                                    value: '',
                                };
                                setCmmConfig({
                                    ...cmmConfig,
                                    validationCells: [...(cmmConfig.validationCells ?? []), newCell],
                                });
                            }}
                        />
                    </div>
                    <ValidationCellsTable
                        validationCells={cmmConfig.validationCells ?? []}
                        onChange={(newCells) => setCmmConfig({ ...cmmConfig, validationCells: newCells })}
                    />
                </div>

                <div className='dialog-form__field--with-margins'>
                    <div className='flex--horz' style={{ justifyContent: 'space-between' }}>
                        <div>Equipment name cell</div>
                        {!cmmConfig.equipmentNameCell && (
                            <LinkButton children='Add' onClick={() => setCmmConfig({ ...cmmConfig, equipmentNameCell: '' })} />
                        )}
                    </div>
                    {cmmConfig.equipmentNameCell != null && (
                        <table style={{ marginTop: '3px' }}>
                            <tbody>
                                <tr>
                                    <td style={{ border: '0px', textAlign: 'left' }}>
                                        <input
                                            style={{ width: '50px' }}
                                            type='text'
                                            pattern='^[A-Z]+[1-9][0-9]*$'
                                            value={cmmConfig.equipmentNameCell}
                                            onChange={(e) => setCmmConfig({ ...cmmConfig, equipmentNameCell: e.target.value.toUpperCase() })}
                                        />
                                    </td>
                                    <td style={{ border: '0px', textAlign: 'right' }}>
                                        <LinkButton children='Remove' onClick={() => setCmmConfig({ ...cmmConfig, equipmentNameCell: undefined })} />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>

                <div className='dialog--button-bar'>
                    <button type='button' onClick={onClose}>
                        Cancel
                    </button>
                    <button type='submit' disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </form>
        </ModalDialog>
    );
}


function ValidationCellsTable({
    validationCells,
    onChange,
}: {
    validationCells: { coordinates: string; value: string }[];
    onChange: (cells: { coordinates: string; value: string }[]) => void;
}) {
    const handleCoordinateChange = (index: number, coordinates: string) => {
        const newCells = [...validationCells];
        newCells[index] = { ...newCells[index], coordinates: coordinates.toUpperCase() };
        onChange(newCells);
    }

    const handleValueChange = (index: number, value: string) => {
        const newCells = [...validationCells];
        newCells[index] = { ...newCells[index], value };
        onChange(newCells);
    }

    const handleCellRemove = (index: number) => {
        const newCells = [...validationCells];
        newCells.splice(index, 1);
        onChange(newCells);
    };

    return (
        <table style={{ marginTop: '3px' }}>
            <tbody>
                {validationCells.map((cell, index) => (
                    <tr key={index}>
                        <td style={{ border: '0px' }}>
                            <input
                                style={{ width: '50px' }}
                                type='text'
                                value={cell.coordinates}
                                onChange={e => handleCoordinateChange(index, e.target.value)}
                            />
                        </td>
                        <td style={{ border: '0px' }}>
                            <input
                                style={{ width: '100%' }}
                                type='text'
                                value={cell.value}
                                onChange={e => handleValueChange(index, e.target.value)}
                            />
                        </td>
                        <td style={{ border: '0px' }}>
                            <LinkButton children='Remove' onClick={() => handleCellRemove(index)} />
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
